import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard, ShoppingBag, Package, Tag, Megaphone,
  Ticket, MapPin, BarChart3, Menu, X, Bell, LogOut, ChevronLeft
} from 'lucide-react';
import { useAuthStore, useUIStore } from '../../store';
import { authApi } from '../../api/services';
import toast from 'react-hot-toast';
import * as signalR from '@microsoft/signalr';
import './AdminLayout.css';

const navItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/admin/orders', label: 'Siparişler', icon: ShoppingBag },
  { path: '/admin/products', label: 'Ürünler', icon: Package },
  { path: '/admin/categories', label: 'Kategoriler', icon: Tag },
  { path: '/admin/campaigns', label: 'Kampanyalar', icon: Megaphone },
  { path: '/admin/coupons', label: 'Kuponlar', icon: Ticket },
  { path: '/admin/neighborhoods', label: 'Mahalleler', icon: MapPin },
  { path: '/admin/reports', label: 'Raporlar', icon: BarChart3 },
];

export default function AdminLayout() {
  const { sidebarOpen, toggleSidebar } = useUIStore();
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [newOrderCount, setNewOrderCount] = useState(0);
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null);

  // SignalR connection
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:5000'}/hubs/orders?access_token=${token}`)
      .withAutomaticReconnect()
      .build();

    conn.on('new-order', (_order) => {
      setNewOrderCount((c) => c + 1);
      toast.success('🔔 Yeni sipariş geldi!', { duration: 5000 });
      // Play sound
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAA==');
        audio.play().catch(() => {});
      } catch {}
    });

    conn.start().catch(() => {});
    setConnection(conn);

    return () => { conn.stop(); };
  }, []);

  const isActive = (path: string, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    try {
      const accessToken = localStorage.getItem('access_token') || '';
      const refreshToken = localStorage.getItem('refresh_token') || '';
      await authApi.logout({ accessToken, refreshToken });
    } catch {}
    logout();
    navigate('/login');
  };

  return (
    <div className={`admin-layout ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
      {/* ─── Sidebar ─── */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <Link to="/" className="sidebar-brand">
            {sidebarOpen ? (
              <img src="/logo-transparent.png" alt="Zavadak" className="sidebar-logo-img" />
            ) : (
              <span className="sidebar-brand-collapsed">Z</span>
            )}
          </Link>
          <button className="sidebar-toggle" onClick={toggleSidebar}>
            {sidebarOpen ? <ChevronLeft size={18} /> : <Menu size={18} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.exact);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-nav-item ${active ? 'active' : ''}`}
                title={!sidebarOpen ? item.label : undefined}
              >
                <div className="nav-item-icon">
                  <Icon size={18} />
                  {item.path === '/admin/orders' && newOrderCount > 0 && (
                    <span className="nav-badge">{newOrderCount}</span>
                  )}
                </div>
                {sidebarOpen && <span>{item.label}</span>}
                {active && <div className="nav-active-indicator" />}
              </Link>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className={`sidebar-user ${!sidebarOpen ? 'collapsed' : ''}`}>
            <div className="user-avatar">
              {user?.role?.[0] ?? 'A'}
            </div>
            {sidebarOpen && (
              <div className="user-info">
                <span className="user-role">{user?.role}</span>
                <span className="user-phone">{user?.phone}</span>
              </div>
            )}
          </div>
          <button className="sidebar-logout" onClick={handleLogout} title="Çıkış">
            <LogOut size={16} />
            {sidebarOpen && <span>Çıkış</span>}
          </button>
        </div>
      </aside>

      {/* ─── Main Content ─── */}
      <div className="admin-main">
        {/* Topbar */}
        <header className="admin-topbar">
          <button className="topbar-menu-btn" onClick={toggleSidebar}>
            <Menu size={20} />
          </button>
          <div className="topbar-title">
            {navItems.find((n) => isActive(n.path, n.exact))?.label ?? 'Admin Panel'}
          </div>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={() => { setNewOrderCount(0); navigate('/admin/orders'); }}>
              <Bell size={18} />
              {newOrderCount > 0 && <span className="topbar-badge">{newOrderCount}</span>}
            </button>
          </div>
        </header>

        {/* Page content */}
        <div className="admin-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
